import { Import } from "lucide-react";
import { ExpiredsIcon } from "src/components/icons";
import useSearchListings from "../../hooks/useSearchListings";
import useSearchListingsPage from "../SearchListings/useSearchListingsPage";
import ExpiredDetails from "../SearchListings/ExpiredDetails";
import FeatureDiscoveryTooltip from "../FeatureDiscoveryTooltip";
import LinkButton from "../core/LinkButton";
import useAccount from "../../hooks/useAccount";
import { useCallback, useMemo } from "react";
import { EnumProductTypes } from "../../types/listing";
import { EXPIRED_PROPERTY_TYPE_OPTIONS, DEFAULT_EXPIRED_PROPERTY_TYPES } from "../../constants/listing";
import { useFeatureDiscovery } from "src/hooks/useFeatureDiscovery";
import SearchPage from "../SearchListings/SearchPage";
import { EnumActionType, MenuItem } from "../neighborhoodSearch/savedSearch/ActionsMenu";
import useIsMobile from "src/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";

const SearchExpireds = () => {
  const navigate = useNavigate();
  const {
    data: account,
    isLoading: isLoadingAccount,
    isError: isErrorLoadingAccount,
  } = useAccount();

  const searchListings = useSearchListings({
    productType: EnumProductTypes.expired,
    searchQueryKey: "getExpiredListings",
    searchUrl: "/api/expired/listing",
    includeListingStatusFilter: true,
    includeBatchIdFilter: true,
    includeMlsFilter: true,
    includeSearchDateTypeFilter: true,
    includePropertyTypeFilter: true,
    propertyTypeOptions: EXPIRED_PROPERTY_TYPE_OPTIONS,
    defaultPropertyTypes: DEFAULT_EXPIRED_PROPERTY_TYPES,
  });

  const features = useMemo(() => ["manage-lookups"], []);

  const { nextFeature, isLastFeature, shouldShowTooltip } =
    useFeatureDiscovery(features);

  const hasExpiredsUploadLicense = useMemo(() => {
    if (isLoadingAccount || isErrorLoadingAccount) {
      return false;
    }
    return account.expired_upload;
  }, [account, isLoadingAccount, isErrorLoadingAccount]);

  const isExpiredPro = useMemo(() => {
    if (isLoadingAccount || isErrorLoadingAccount) {
      return false;
    }
    return account.expired_pro;
  }, [account, isLoadingAccount, isErrorLoadingAccount]);

  const searchListingsPage = useSearchListingsPage({
    totalRecords: searchListings.totalRecords || 0,
    listings: searchListings.listings || [],
    csvApiUrl: searchListings.csvApiUrl,
    allListingsQuery: searchListings.allListingsQuery,
    fetchListingsInChunks: searchListings.fetchListingsInChunks,
    sortField: searchListings.sortField,
    sortDir: searchListings.sortDir,
    searchQueryKey: "getExpiredListings",
    exportedCsvFileName: "expiredlisting",
    productType: EnumProductTypes.expired,
    searchCriteriaKey: searchListings.searchCriteriaKey,
  });

  const isMobile = useIsMobile();

  const handleExpiredUpload = useCallback(() => {
    navigate("/expireds/manage");
  }, [navigate]);

  const additionalActionsMenuItems: MenuItem[] = useMemo(() => {
    if (!isMobile || !hasExpiredsUploadLicense) return [];
    return [
      {
        type: EnumActionType.ExpiredUpload,
        icon: <Import className="h-4 w-4" />,
        label: "Expired Upload",
        onAction: handleExpiredUpload,
      },
    ];
  }, [isMobile, hasExpiredsUploadLicense, handleExpiredUpload]);

  return (
    <SearchPage
      pageTitle="Expireds"
      pageTitleIcon={<ExpiredsIcon />}
      isPro={isExpiredPro}
      searchListings={searchListings}
      searchListingsPage={searchListingsPage}
      additionalActionsMenuItems={additionalActionsMenuItems}
      sendCardsTemplateCategory="Expireds"
      propertyTypeOptions={EXPIRED_PROPERTY_TYPE_OPTIONS}
      pageTitleButtons={
        hasExpiredsUploadLicense ? (
          <div className="flex gap-2">
            <FeatureDiscoveryTooltip
              content="Upload your expired data and view your previous lookups!"
              isOpen={shouldShowTooltip("manage-lookups")}
              onNext={nextFeature}
              isLast={isLastFeature}
            >
              <LinkButton to="/expireds/manage" icon={<Import />} variant="outline">
                Expired Upload
              </LinkButton>
            </FeatureDiscoveryTooltip>
          </div>
        ) : null
      }
      detailsPanelProvider={({
        detailsListingId,
      }) => (
        <ExpiredDetails
          productType={EnumProductTypes.expired}
          isOld={false}
          listingId={detailsListingId}
          isHideDNCPhoneNumbers={searchListings.isHideDNCPhoneNumbers}
        />
      )}
    />
  );
};

export default SearchExpireds;